/**
 * The read-only view behind a share link (CLAUDE.md 11.4, 2.1).
 *
 * Whoever opens this has a token and nothing else: no account, quite possibly no idea
 * what CalcDesk is, and often a phone. Three things follow, and they are the whole
 * design of the page:
 *
 * 1. **Nothing here is actionable.** No inputs, no editing, no navigation to a
 *    sibling position or project, no sign-in prompt. A share link is a window, not a
 *    door.
 * 2. **The document status is stated, not implied.** The watermark says what this
 *    calculation is fit for -- and only an approved position may say FOR CONSTRUCTION
 *    (section 2.2). A checker who assumes a draft is issued work is exactly the
 *    failure the review workflow exists to prevent.
 * 3. **The responsibility statement travels with the calculation** (section 2.1),
 *    because for this reader the page *is* the deliverable.
 */
import { useState, type FormEvent } from 'react'
import { useParams } from 'react-router-dom'
import { Download, Lock, ShieldAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import { useSharedView } from '@/api/hooks'
import type { SharedPosition, SharedView } from '@/api/types'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card'
import { Field, FieldLabel } from '@/components/ui/field'
import { FieldInput } from '@/components/ui/input'
import { LanguageToggle } from '@/components/ui/LanguageToggle'
import { Skeleton } from '@/components/ui/skeleton'
import { StatusBadge, type Status } from '@/components/ui/StatusBadge'
import { ThemeToggle } from '@/components/ui/ThemeToggle'
import { UtilisationBar } from '@/components/ui/UtilisationBar'
import { downloadFile } from '@/lib/download'
import { formatDateTime, formatUtilisation } from '@/lib/format'

function badgeStatus(status: string): Status {
  if (status === 'pass') return 'pass'
  if (status === 'fail') return 'fail'
  if (status === 'stale' || status === 'error') return 'warning'
  return 'not_applicable'
}

/** The status band. Deliberately loud: it is the first thing the reader must take in. */
function DocStatusBanner({ status }: { status: SharedView['doc_status'] }) {
  const { t } = useTranslation()
  const tone =
    status === 'construction'
      ? 'bg-[var(--status-pass-bg)] text-[var(--status-pass)]'
      : status === 'review'
        ? 'bg-[var(--status-warn-bg)] text-[var(--status-warn)]'
        : 'bg-surface-raised text-text-muted'

  return (
    <div className={`rounded-md px-3 py-2 text-sm font-semibold uppercase tracking-wide ${tone}`}>
      {t(`share.doc_status.${status}`)}
    </div>
  )
}

export function SharedPage() {
  const { t, i18n } = useTranslation()
  const { token } = useParams()
  const [password, setPassword] = useState<string | null>(null)

  const { data, error, isLoading } = useSharedView(token, password)

  const needsPassword =
    error instanceof ApiError &&
    error.status === 401 &&
    (error.body.message_key === 'error.share.password_required' ||
      error.body.message_key === 'error.share.password_invalid')

  return (
    <div className="flex min-h-dvh flex-col bg-bg text-text">
      <header className="flex h-14 shrink-0 items-center border-b border-border px-4">
        <span className="font-semibold">{t('app.name')}</span>
        <span className="ms-2 rounded bg-surface-raised px-1.5 py-0.5 text-xs text-text-muted">
          {t('share.read_only')}
        </span>
        <div className="ms-auto flex items-center gap-1">
          <LanguageToggle />
          <ThemeToggle />
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 space-y-4 p-4">
        {isLoading && <Skeleton className="h-40 w-full" />}

        {needsPassword && (
          <PasswordGate
            invalid={
              error instanceof ApiError && error.body.message_key === 'error.share.password_invalid'
            }
            onSubmit={setPassword}
          />
        )}

        {error && !needsPassword && (
          <Card>
            <CardBody className="py-10 text-center">
              <ShieldAlert size={28} className="mx-auto mb-2 text-text-muted" aria-hidden />
              <p className="font-medium">{t('share.unavailable')}</p>
              <p className="mt-1 text-sm text-text-muted">
                {error instanceof ApiError ? error.localised : t('share.unavailable_hint')}
              </p>
            </CardBody>
          </Card>
        )}

        {data && (
          <>
            <DocStatusBanner status={data.doc_status} />

            <div>
              <h1 className="text-lg font-semibold">{data.project.name}</h1>
              <p className="text-sm text-text-muted">
                {[data.project.number, data.project.client].filter(Boolean).join(' · ') || '—'}
                {' · '}
                {data.project.code_id} {data.project.param_set}
              </p>
              <p className="mt-1 text-xs text-text-muted">
                {t('share.shared_by', { organisation: data.organisation })}
                {data.expires_at
                  ? ` · ${t('share.expires', {
                      date: formatDateTime(data.expires_at, i18n.language),
                    })}`
                  : ''}
              </p>
            </div>

            {data.scope === 'project' && <PositionList positions={data.positions} />}
            {data.scope === 'position' && data.position && (
              <PositionCard position={data.position} />
            )}
            {data.scope === 'report' && data.report && (
              <ReportCard report={data.report} token={token} password={password} />
            )}

            {/* Section 2.1: the responsibility statement travels with the output. */}
            <Card>
              <CardBody>
                <p className="text-xs leading-relaxed text-text-muted">{t('share.disclaimer')}</p>
              </CardBody>
            </Card>
          </>
        )}
      </main>
    </div>
  )
}

function PasswordGate({
  invalid,
  onSubmit,
}: {
  invalid: boolean
  onSubmit: (password: string) => void
}) {
  const { t } = useTranslation()
  const [value, setValue] = useState('')

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (value) onSubmit(value)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock size={16} aria-hidden />
          {t('share.password_title')}
        </CardTitle>
      </CardHeader>
      <CardBody>
        <form onSubmit={submit} className="space-y-3">
          {/* `Field` owns the label/error wiring: `aria-describedby` and
              `aria-invalid` land on the control without being spelled here (14.5). */}
          <Field error={invalid ? t('share.password_invalid') : null}>
            <FieldLabel>{t('share.password_label')}</FieldLabel>
            <FieldInput
              type="password"
              value={value}
              onChange={(event) => setValue(event.target.value)}
            />
          </Field>
          <Button type="submit" variant="primary" disabled={!value}>
            {t('action.open')}
          </Button>
        </form>
      </CardBody>
    </Card>
  )
}

function PositionList({ positions }: { positions: SharedPosition[] }) {
  const { t } = useTranslation()

  if (positions.length === 0) {
    return (
      <Card>
        <CardBody className="py-8 text-center text-sm text-text-muted">
          {t('empty.no_positions')}
        </CardBody>
      </Card>
    )
  }

  return (
    <ul className="space-y-2">
      {positions.map((position) => (
        <li key={position.id}>
          <PositionCard position={position} compact />
        </li>
      ))}
    </ul>
  )
}

function PositionCard({
  position,
  compact = false,
}: {
  position: SharedPosition
  compact?: boolean
}) {
  const { t } = useTranslation(['common', 'report'])

  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="tabular w-10 shrink-0 text-sm text-text-muted">
            {`P${String(position.seq).padStart(2, '0')}`}
          </span>
          <span className="rounded bg-surface-raised px-1.5 py-0.5 text-xs font-medium">
            {position.short_code}
          </span>
          <span className="min-w-0 flex-1 truncate font-medium">{position.name}</span>
          {position.governing_ratio != null && (
            <span className="tabular text-sm text-text-muted">
              {formatUtilisation(position.governing_ratio)}
            </span>
          )}
          <StatusBadge status={badgeStatus(position.status)} />
        </div>

        {/* The version quadruple of section 5.7, so the reader can tell exactly what
            produced these numbers. */}
        <p className="text-xs text-text-muted">
          {position.module_id} v{position.module_version} · {position.code_id}{' '}
          {position.param_set_version}
        </p>

        {!compact && position.checks.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[32rem] text-sm">
              <thead>
                <tr className="border-b border-border text-start text-xs text-text-muted">
                  <th className="py-1 text-start font-medium">{t('common.check')}</th>
                  <th className="py-1 text-start font-medium">{t('common.clause')}</th>
                  <th className="py-1 text-end font-medium">{t('common.demand')}</th>
                  <th className="py-1 text-end font-medium">{t('common.capacity')}</th>
                  <th className="py-1 text-end font-medium">{t('common.utilisation')}</th>
                </tr>
              </thead>
              <tbody>
                {position.checks.map((check) => (
                  <tr key={check.key} className="border-b border-border/50">
                    <td className="py-1.5">{t(check.name_key, { ns: 'report' })}</td>
                    <td className="py-1.5 text-text-muted">{check.clause}</td>
                    <td className="tabular py-1.5 text-end">{check.demand}</td>
                    <td className="tabular py-1.5 text-end">{check.capacity}</td>
                    <td className="py-1.5 text-end">
                      <UtilisationBar ratio={check.ratio} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

function ReportCard({
  report,
  token,
  password,
}: {
  report: NonNullable<SharedView['report']>
  token: string | undefined
  password: string | null
}) {
  const { t, i18n } = useTranslation()
  const [downloading, setDownloading] = useState(false)
  const [failed, setFailed] = useState(false)

  /**
   * Fetched rather than linked: a password-protected link carries its credential in a
   * header, and an `<a href>` cannot. `auth: false` because this reader has no
   * account -- sending a stale bearer token from a previous session would be the
   * wrong identity, not a fallback.
   */
  const download = async () => {
    setDownloading(true)
    setFailed(false)
    try {
      const ok = await downloadFile(`/api/v1/s/${token}/file`, {
        auth: false,
        headers: password ? { 'X-Share-Password': password } : undefined,
      })
      setFailed(!ok)
    } catch {
      setFailed(true)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Card>
      <CardBody className="space-y-3">
        <p className="text-sm">
          {t('share.report_created', {
            date: formatDateTime(report.created_at, i18n.language),
          })}
          {report.page_count ? ` · ${t('share.report_pages', { count: report.page_count })}` : ''}
        </p>

        <Button variant="primary" onClick={download} loading={downloading} disabled={!report.ready}>
          <Download size={16} aria-hidden />
          {t('action.download')}
        </Button>

        {!report.ready && <p className="text-sm text-text-muted">{t('share.report_pending')}</p>}
        {failed && <p className="text-sm text-[var(--status-fail)]">{t('share.unavailable')}</p>}
      </CardBody>
    </Card>
  )
}
